<script>
import VueMultiselect from 'vue-multiselect'
import ClientForm from "@/components/forms/ClientForm.vue";
import { useI18n } from 'vue-i18n';
import helpers from "@root/helpers.js";
import {log} from "@/utils/clientLogger.js";

export default {
  name: "OrderActors",
  components: { VueMultiselect, ClientForm },
  props: {
    clientData: {
      type: Object,
      default: () => ({})
    },
    tempClients: {
      type: Array,
      default: () => []
    },
    addClient: {
      type: Boolean,
      default: false
    },
    managerData: {
      type: Object,
      default: () => ({})
    },
    tempUsers: {
      type: Array,
      default: () => []
    },
    orderId: {
      type: String,
      default: null
    },
    isDistributor: {
      type: Boolean,
      default: false
    },
    isOrderFromDistributor: {
      type: Boolean,
      default: false
    },
    currentUserRole: {
      type: String,
      default: ''
    }
  },
  emits: [
    'update:clientData',
    'update:managerData',
    'update:addClient',
    'search-clients',
    'search-managers',
    'map-changes'
  ],
  setup() {
    const {t} = useI18n();
    return {t};
  },
  data() {
    return {
      clientSearchTimer: null,
      managerSearchTimer: null,
      isLoadingClients: false,
      isLoadingManagers: false,
      isOpen: true,
      notesOpen: true,
      newNote: '',
      savingNote: false
    }
  },
  computed: {
    filteredClients() {
      if (this.isDistributor) {
        return this.tempClients.filter(client => client.accessStatus !== 'pending');
      }
      return this.tempClients;
    }
  },
  watch: {
    tempClients() {
      this.isLoadingClients = false;
    },
    tempUsers() {
      this.isLoadingManagers = false;
    }
  },
  methods: {
    formatNoteDate(date) {
      if (!date) return '';
      return helpers.formatFullDate(date);
    },
    async addClientNote() {
      const text = (this.newNote || '').trim();
      if (!text || !this.clientData?._id || this.savingNote) return;

      this.savingNote = true;
      try {
        const res = await this.$http.post(`/api/clients/${this.clientData._id}/notes`, {text});
        this.$emit('update:clientData', {...this.clientData, notes: res.data.notes});
        this.newNote = '';
        this.notesOpen = true;
      } catch (e) {
        log.error('OrderActors:addClientNote', {error: e});
        this.$swal.fire(this.t('alerts.error'), e.response?.data?.message || '', 'error');
      } finally {
        this.savingNote = false;
      }
    },
    async requestDeleteNote(note) {
      if (!note?._id || !this.clientData?._id) return;

      const confirm = await this.$swal.fire({
        title: this.t('asks.deleteNoteTitle', 'Видалити примітку?'),
        text: note.text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.t('buttons.delete'),
        cancelButtonText: this.t('buttons.cancel')
      });
      if (!confirm.isConfirmed) return;

      try {
        const res = await this.$http.delete(`/api/clients/${this.clientData._id}/notes/${note._id}`);
        this.$emit('update:clientData', {...this.clientData, notes: res.data.notes});
      } catch (e) {
        log.error('OrderActors:deleteNote', {error: e});
        this.$swal.fire(this.t('alerts.error'), e.response?.data?.message || '', 'error');
      }
    },
    toggleAddClient() {
      const willOpen = !this.addClient;
      this.$emit('update:addClient', willOpen);

      if (willOpen && this.clientData && this.clientData._id) {
        this.$emit('update:clientData', null);
        this.$emit('map-changes', 'clientId');
      }
    },
    updateClient(value) {
      this.$emit('update:clientData', value);
      this.$emit('map-changes', 'clientId');
    },
    updateManager(value) {
      this.$emit('update:managerData', value);
      this.$emit('map-changes', 'managerId');
    },

    onSearchClients(query) {
      this.isLoadingClients = true;
      if (this.clientSearchTimer) clearTimeout(this.clientSearchTimer);

      this.clientSearchTimer = setTimeout(() => {
        this.$emit('search-clients', query);
      }, 300);
    },
    onOpenClients() {
      if (!this.tempClients || this.tempClients.length === 0) {
        this.isLoadingClients = true;
        this.$emit('search-clients', '');
      }
    },

    onSearchManagers(query) {
      this.isLoadingManagers = true;
      if (this.managerSearchTimer) clearTimeout(this.managerSearchTimer);

      this.managerSearchTimer = setTimeout(() => {
        this.$emit('search-managers', query);
      }, 300);
    },
    onOpenManagers() {
      if (!this.tempUsers || this.tempUsers.length === 0) {
        this.isLoadingManagers = true;
        this.$emit('search-managers', '');
      }
    }
  }
}
</script>

<template>
  <div class="col-12">
    <div class="card shadow-sm border-0 section-card">
      <div class="card-header bg-light d-flex align-items-center justify-content-between"
           @click="isOpen = !isOpen" style="cursor: pointer; user-select: none;">
        <span class="fw-semibold d-flex align-items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-primary">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" />
            <path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            <path d="M21 21v-2a4 4 0 0 0 -3 -3.85" />
          </svg>
          {{ t('headings.actors', 'Клієнт та менеджер') }}
        </span>
        <svg :class="{'collapse-arrow-rotated': !isOpen}" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted" style="transition: transform 0.2s ease;">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M6 9l6 6l6 -6" />
        </svg>
      </div>
      <div :class="{ 'collapse': true, 'show': isOpen }">
        <div class="card-body">
        <div class="mb-2">
          <label class="form-label small text-muted mb-1">{{ t('tableheadings.client') }}</label>
          <div class="d-flex align-items-center gap-2">
            <div class="flex-grow-1">
              <VueMultiselect
                  :model-value="clientData"
                  :options="filteredClients"
                  :close-on-select="true"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :internal-search="false"
                  :loading="isLoadingClients"
                  @update:modelValue="updateClient"
                  @search-change="onSearchClients"
                  @open="onOpenClients"
                  :placeholder="t('placeholders.selectClient')"
                  label="name"
                  track-by="name"
              >
                <template #noResult>
                  {{ t('messages.noResults', 'Нічого не знайдено. Спробуйте змінити запит.') }}
                </template>

                <template #afterList>
                  <div class="p-2 text-center text-muted" style="font-size: 12px; opacity: 0.7;">
                    <span v-if="filteredClients.length >= 50">
                      {{ t('messages.firstSearchResults', 'Показано перші 50 результатів. Продовжуйте писати для уточнення...') }}
                    </span>
                  </div>
                </template>
              </VueMultiselect>
            </div>

            <div style="cursor: pointer" @click="toggleAddClient" class="flex-shrink-0 text-center" :title="addClient ? t('buttons.cancel') : t('buttons.addClient')">
              <template v-if="addClient">
                <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-x text-danger" width="22" height="22" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M18 6l-12 12"/>
                  <path d="M6 6l12 12"/>
                </svg>
              </template>
              <template v-else>
                <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-user-plus text-primary" width="22" height="22" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"/>
                  <path d="M16 19h6"/>
                  <path d="M19 16v6"/>
                  <path d="M6 21v-2a4 4 0 0 1 4 -4h4"/>
                </svg>
              </template>
            </div>
          </div>
        </div>

        <div v-if="clientData && clientData._id" class="client-notes-inline mb-2">
          <button type="button" class="cn-toggle" @click="notesOpen = !notesOpen">
            <span class="d-flex align-items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-warning">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                <path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" />
                <path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" />
                <path d="M9 12h6" />
                <path d="M9 16h6" />
              </svg>
              {{ t('labels.clientNotes', 'Примітки клієнта') }}
              <span v-if="clientData.notes && clientData.notes.length" class="badge bg-warning text-dark rounded-pill">{{ clientData.notes.length }}</span>
            </span>
            <svg :class="{'cn-chevron-rotated': !notesOpen}" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M6 9l6 6l6 -6" />
            </svg>
          </button>

          <div v-show="notesOpen" class="cn-list">
            <div v-for="(note, index) in clientData.notes" :key="note._id || index" class="cn-item">
              <div class="cn-body">
                <div class="cn-text text-break">{{ note.text }}</div>
                <div v-if="note.author || note.createdAt" class="cn-meta">
                  <template v-if="note.author">{{ note.author }}</template>
                  <template v-if="note.author && note.createdAt"> • </template>
                  <template v-if="note.createdAt">{{ formatNoteDate(note.createdAt) }}</template>
                </div>
              </div>
              <button v-if="note._id" type="button" class="cn-del" :title="t('buttons.delete')" @click="requestDeleteNote(note)">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M4 7l16 0" />
                  <path d="M10 11l0 6" />
                  <path d="M14 11l0 6" />
                  <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
                  <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
                </svg>
              </button>
            </div>

            <p v-if="!clientData.notes || !clientData.notes.length" class="cn-empty">
              {{ t('messages.noClientNotes', 'Приміток ще немає') }}
            </p>

            <div class="cn-add">
              <input type="text" class="form-control form-control-sm" v-model="newNote"
                     :placeholder="t('placeholders.clientNote', 'Напр.: Завжди без поворотників')"
                     @keydown.enter.prevent="addClientNote">
              <button type="button" class="btn btn-warning btn-sm d-flex align-items-center justify-content-center gap-1"
                      :disabled="!newNote.trim() || savingNote" @click="addClientNote">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M12 5l0 14" />
                  <path d="M5 12l14 0" />
                </svg>
                {{ t('buttons.addNote', 'Додати примітку') }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="addClient" class="mt-3">
          <ClientForm @submit-client="toggleAddClient"/>
        </div>

        <div class="mt-3" v-if="!isDistributor">
          <label class="form-label small text-muted mb-1">
            {{ isOrderFromDistributor ? t('tableheadings.distributor') : t('tableheadings.manager') }}
            <sup v-if="!orderId">
              <small>{{ t('labels.addAutomatically') }}</small>
            </sup>
          </label>
          <VueMultiselect
              :model-value="managerData"
              :options="tempUsers"
              :close-on-select="true"
              :clear-on-select="false"
              :disabled="currentUserRole === 'draftsman'"
              :preserve-search="true"
              :internal-search="false"
              :loading="isLoadingManagers"
              @update:modelValue="updateManager"
              @search-change="onSearchManagers"
              @open="onOpenManagers"
              :placeholder="t('placeholders.selectManager')"
              label="username"
              track-by="username"
          />
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style scoped lang="scss">
small {
  font-size: 10px;
}

.section-card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;

  .card-header {
    border-bottom: 1px solid #e9ecef;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    font-size: 0.95rem;
  }
}

.collapse-arrow-rotated {
  transform: rotate(-90deg);
}

.client-notes-inline {
  border: 1px solid #ffe39a;
  border-radius: 0.5rem;
  background: #fffdf6;
  overflow: hidden;

  .cn-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 0.65rem;
    border: none;
    background: #fff8e6;
    font-weight: 600;
    font-size: 0.9rem;
    color: #6c5400;
    cursor: pointer;

    .cn-chevron-rotated {
      transform: rotate(-90deg);
    }

    svg {
      transition: transform 0.2s ease;
    }
  }

  .cn-list {
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .cn-item {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    padding: 0.4rem 0.55rem;
    background: #fff;
    border: 1px solid #ffe39a;
    border-left: 3px solid #ffc107;
    border-radius: 0.35rem;

    .cn-body {
      flex-grow: 1;
      min-width: 0;
    }

    .cn-text {
      font-size: 0.85rem;
      color: #212529;
      white-space: pre-wrap;
      line-height: 1.3;
    }

    .cn-meta {
      font-size: 0.7rem;
      color: #adb5bd;
      margin-top: 2px;
    }

    .cn-del {
      flex-shrink: 0;
      align-self: center;
      border: none;
      background: transparent;
      color: #c0c4c8;
      padding: 0;
      line-height: 1;
      transition: color 0.15s ease;

      &:hover {
        color: #dc3545;
      }
    }
  }

  .cn-empty {
    font-size: 0.8rem;
    font-style: italic;
    color: #adb5bd;
    margin: 0;
  }

  .cn-add {
    display: flex;
    gap: 0.4rem;
    margin-top: 0.2rem;

    .form-control {
      flex: 1 1 auto;
    }

    .btn {
      flex-shrink: 0;
      white-space: nowrap;
    }
  }
}
</style>